import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/LoginView.vue"),
    meta: {
      title: "Sign In - ScantixPro",
      requiresAuth: false,
    },
  },
  {
    path: "/",
    name: "Home",
    component: () => import("@/views/HomeView.vue"),
    meta: {
      title: "ScantixPro Scanner",
      requiresAuth: true,
    },
  },
  {
    path: "/scanner",
    name: "Scanner",
    component: () => import("@/views/ScannerView.vue"),
    meta: {
      title: "Barcode Scanner",
      requiresAuth: true,
    },
  },
  {
    path: "/scan-result/:barcode/:mode?",
    name: "ScanResult",
    component: () => import("@/views/ScanResultView.vue"),
    meta: {
      title: "Scan Result",
      requiresAuth: true,
    },
  },
  {
    path: "/results",
    name: "Results",
    component: () => import("@/views/ResultsView.vue"),
    meta: {
      title: "Scan Results",
      requiresAuth: true,
    },
  },
  {
    path: "/reports",
    name: "Reports",
    component: () => import("@/views/ReportsView.vue"),
    meta: {
      title: "Reports",
      requiresAuth: true,
    },
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/views/SettingsView.vue"),
    meta: {
      title: "Settings",
      requiresAuth: true,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/NotFoundView.vue"),
    meta: {
      title: "Page Not Found",
      requiresAuth: false,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// Global navigation guard for authentication and page titles
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Set page title
  const title = to.meta?.title as string;
  if (title) {
    document.title = title;
  }

  // Application enabled/disabled check is now handled server-side in index.php
  // Vue app no longer needs to check enabled state

  // Check if route requires authentication
  const requiresAuth = to.meta?.requiresAuth !== false;

  // If going to login and already authenticated, redirect to home
  if (to.name === "Login" && authStore.isAuthenticated) {
    next({ name: "Home" });
    return;
  }

  // If route requires auth and user is not authenticated, redirect to login
  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: "Login" });
    return;
  }

  // Allow navigation
  next();
});

export default router;
